import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Container,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useNoAuth } from "../../hooks/useNoAuth";
import axios from "axios";
import { AppResources, AppRoutes } from "../../constants";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const UNINSTALL_ENDPOINT = `${BACKEND_URL}/api/extension/uninstalled/`;
const REFRESH_ENDPOINT = `${BACKEND_URL}/api/auth/token/refresh/`;

/**
 * Extension uninstall survey page.
 *
 * This page is shown when users uninstall the Chrome extension via
 * chrome.runtime.setUninstallURL(). It records the uninstallation
 * and optionally collects feedback.
 */
const ExtensionUninstalled = () => {
  // Register this component as not requiring auth
  useNoAuth("ExtensionUninstalled");
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      await axios.post(
        REFRESH_ENDPOINT,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      return true;
    } catch (error) {
      console.log("Could not refresh access token for uninstall flow:", error);
      return false;
    }
  };

  const submitUninstallStatus = async (
    payload: Record<string, unknown>,
    allowRetry = true
  ): Promise<boolean> => {
    try {
      await axios.post(UNINSTALL_ENDPOINT, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      return true;
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;

      if (allowRetry && status && [401, 403].includes(status)) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          return submitUninstallStatus(payload, false);
        }
      }

      console.log("Could not submit uninstall status:", error);
      return false;
    }
  };

  useEffect(() => {
    // Record uninstallation on page load
    const recordUninstall = async () => {
      await submitUninstallStatus({});
    };

    recordUninstall();
  }, []);

  useEffect(() => {
    if (!submitted) return;

    const timer = setTimeout(() => {
      navigate(AppRoutes.login, { replace: true });
    }, 3000);

    return () => clearTimeout(timer);
  }, [submitted, navigate]);

  const handleSubmitFeedback = async () => {
    const feedbackText = feedback.trim();
    if (!feedbackText) {
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    const success = await submitUninstallStatus({ feedback: feedbackText });

    if (success) {
      setSubmitted(true);
    } else {
      setErrorMessage(
        "We couldn't save your feedback. Please try again in a moment."
      );
    }

    setSubmitting(false);
  };

  if (submitted) {
    return (
      <Center h="100vh" w="100vw" bg="gray.0">
        <Container size="sm">
          <Stack align="center" gap="xl">
            <Image
              src={AppResources.wizzyFeedback}
              alt="SmartWiz feedback illustration"
              w={240}
              mah={260}
              fit="contain"
            />
            <Title order={2} ta="center">
              Thank you for your feedback!
            </Title>
            <Text ta="center" c="dimmed" size="lg">
              We appreciate you taking the time to let us know why you
              uninstalled SmartSync. Your feedback helps us improve.
            </Text>
            <Text ta="center" size="sm" c="dimmed">
              If you change your mind, you can reinstall the extension anytime
              from the Chrome Web Store.
            </Text>
            <Group gap="sm" align="center">
              <Loader size="sm" color="blue" />
              <Text size="sm" c="dimmed">
                Taking you back to login...
              </Text>
            </Group>
          </Stack>
        </Container>
      </Center>
    );
  }

  return (
    <Center h="100vh" w="100vw" bg="gray.0">
      <Container size="lg">
        <SimpleGrid
          cols={{ base: 1, md: 2 }}
          spacing={{ base: "xl", md: "2xl" }}
          verticalSpacing="xl"
        >
          {/* Ensures the image is always centered on small screens */}
          <Center
            w="100%"
            h="100%"
            visibleFrom="base"
            style={{
              alignItems: "center",
              // SimpleGrid's default 'display: grid' will handle alignment at md+
              // but we want explicit centering on col=1 layouts (mobile)
            }}
          >
            <Image
              src={AppResources.wizzyFeedback}
              alt="SmartWiz feedback illustration"
              w={{ base: 240, sm: 320, md: 400, lg: 600 }}
              mah={{ base: 180, sm: 240, md: 300, lg: 400 }}
              fit="contain"
            />
          </Center>

          <Stack gap="lg">
            <Title order={1} ta={{ base: "center", md: "left" }}>
              Sorry to see you go!
            </Title>
            <Text ta={{ base: "center", md: "left" }} c="dimmed" size="lg">
              We noticed you uninstalled the SmartSync Chrome Extension.
            </Text>
            <Box>
              <Title order={3} size="h4" mb="md">
                Help us improve
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Would you mind sharing why you uninstalled? Your feedback helps
                us make SmartSync better for everyone.
              </Text>

              <Textarea
                placeholder="Tell us what went wrong or what features you were missing..."
                minRows={4}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                mb="lg"
              />

              {errorMessage && (
                <Text c="red.6" size="sm" mb="sm">
                  {errorMessage}
                </Text>
              )}

              <Group justify="space-between">
                <Button
                  variant="subtle"
                  onClick={() => setSubmitted(true)}
                  color="gray"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleSubmitFeedback}
                  disabled={!feedback.trim() || submitting}
                  loading={submitting}
                >
                  Submit Feedback
                </Button>
              </Group>
            </Box>

            <Box bg="blue.0" p="md" style={{ borderRadius: 8 }}>
              <Title order={4} size="h5" mb="xs">
                Changed your mind?
              </Title>
              <Text size="sm" mb="md">
                You can reinstall SmartSync anytime from the Chrome Web Store.
              </Text>
              <Button
                component="a"
                href="https://chromewebstore.google.com/detail/lopfgdkmhlpffjbcehlcipigognpaada"
                target="_blank"
                variant="light"
                size="sm"
              >
                Reinstall Extension
              </Button>
            </Box>
          </Stack>
        </SimpleGrid>
      </Container>
    </Center>
  );
};

export default ExtensionUninstalled;
